/******************************************************************************
 * 
 * Strauss Motion
 * 
 * Copyright (c) 2022 Robimo GmbH
 *
 * Authors: Daniel Martinek
 *
 *****************************************************************************/
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Container, Dropdown, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { ConfigState, CountryData, LibraryData, LogoFile, LogoType, ProductData, ProductState, StraussLogoType, UIState } from '../store/interfaces';
 
import {isMobile} from 'react-device-detect';

import styles from '../styles/editscreen.module.scss'
import { LogoText } from './LogoText';
import { Preview, PreviewCallbacks, PreviewContainer, PreviewContainerModel } from './Preview';
import {ReactComponent as IconStandard} from '../icons/10_micro_box_no_type_rgb_pos.svg';
import {ReactComponent as IconBlack} from '../icons/10_micro_box_no_type_black_pos.svg';
import {ReactComponent as IconWhite} from '../icons/10_micro_box_no_type_white_border_pos.svg';
import {ReactComponent as IconClose} from '../icons/close.svg';
import {ReactComponent as IconPlus} from '../icons/plus.svg';
import {ReactComponent as IconTrash} from '../icons/remove.svg';
import {ReactComponent as IconPhone} from '../icons/icon_phone.svg';
import {ReactComponent as IconGo} from '../icons/arrow_go_black.svg';


import {ReactComponent as BackgroundSprite1} from '../icons/color_sprite_bg_1.svg';
import {ReactComponent as BackgroundSprite2} from '../icons/color_sprite_bg_2.svg';

import {ReactComponent as IconChange} from '../icons/change.svg';
import {ReactComponent as IconCart} from '../icons/cart.svg';
import {ReactComponent as IconCartBlack} from '../icons/cartBlack.svg';
import {ReactComponent as IconSave} from '../icons/save.svg';
import {ReactComponent as IconShirt} from '../icons/logo.svg';
import {ReactComponent as IconInfo} from '../icons/info.svg';
import {ReactComponent as IconInfoBlack} from '../icons/infoBlack.svg';
import {ReactComponent as IconInfoWhite} from '../icons/infoWhite.svg';

import {ReactComponent as IconArrowLeft} from '../icons/arrow_left.svg';

import {ReactComponent as IconTransfer} from '../icons/transfer.svg';
import {ReactComponent as IconDirect} from '../icons/direct.svg';

import { translate } from '../i18n';
import { Exitscreen } from './Exitscreen';
import { copyTextToClipboard, generateBackgroundSplashBaseFill, generateBackgroundSplashContrastFill, generateBackgroundStyle, GetBasicImageLink, GetBasicsLink, getMinimumOrderAmount, IsBaseColorAllowed, IsContrastColorAllowed } from '../helpers/helpers';
import { applyVat, getPricingText } from '../helpers/pricing';
import { DropdownButton } from './DropdownButton';
import { getURL, GET_LOGO } from '../store/api';
import OrderscreenContainer from '../containers/OrderscreenContainer';
import CustomerscreenContainer from '../containers/CustomerscreenContainer';
import FinalscreenContainer from '../containers/FinalscreenContainer';
import PostscreenContainer from '../containers/PostscreenContainer';
import LanguageSwitcherContainer from '../containers/LanguageSwitcherContainer';
import { createPortal } from 'react-dom';
import LinkFooterContainer from '../containers/LinkFooterContainer';

declare global {
    interface Window {
        SALES: boolean;
    }
}

const MAX_ALLOWED_LOGO_FILES = 2;

interface EditscreenProps {
    config: ConfigState;
    ui: UIState;
    library: LibraryData;
    startTab?: string;
    onUiGoTo: (route: string) => void;
    onUiSetPreviewArticle1: (article: string) => void;
    onUiSetPreviewArticle2: (article: string) => void;
    onUiSetDetailArticleIndex: (detailArticleIndex: number) => void;
    onUiSetEditScreenTab: (editScreenTab: string) => void;
    onProductAdd: (article: string, size: string) => void;
    onProductUpdate: (index: number, pocket: ProductState) => void;
    onProductRemove: (index: number) => void;
    onConfigSetBaseColor: (colorId: string) => void;
    onConfigSetContrastColor: (colorId: string) => void;
    onConfigSetStraussLogo: (logo: StraussLogoType) => void;
    onConfigStoreLogo: (logo: any) => void;
    onConfigDeleteLogo: (filename: string) => void;
    onUiSetVat: (vat: boolean) => void;
}

interface ProductProps {
    product: ProductData;
    ventilationProduct?: ProductData;
    useVentilation: boolean;
    vat: boolean;
    vatValue: number;
    selectedArticles: string[];
    index: number;
    country: CountryData;
    library: LibraryData;
    config: ConfigState;
    productMultiSelection: boolean;
    divRef: React.RefObject<HTMLDivElement>;
    onUiSetVat: (vat: boolean) => void;
    onUiSetConfigArticle: (article: string) => void;
    onProductAdd: (article: string, size: string) => void;
    onChangeVentilation: (ventilation: boolean) => void;
}

interface LogoUploadProps {
    allowedFileCount: number;
    ui: UIState;
    onConfigStoreLogo: (logo: any) => void;
    onFinishedUpload: () => void;
}

function Product(props: ProductProps) {
    const {t, i18n} = useTranslation();

    const previewProduct = (props.useVentilation && props.ventilationProduct !== undefined) ? props.ventilationProduct : props.product;
    const basePricing = props.vat ? applyVat(previewProduct.pricing[0].pricing, props.vatValue) : previewProduct.pricing[0].pricing;

    const isOnOrderlist = props.config.products.some((productState, index) => {
        return (productState.article === previewProduct?.article);
    });

    const isSelected = props.selectedArticles.includes(previewProduct.article);

    return <div className={`${styles.product} ${isSelected ? styles.productSelected : ''}`} key={props.index}>
        <div className={styles.productPreview} ref={props.divRef}>      
          
        </div>
        <div className={styles.productInfo}>
            <h1>{translate(props.product.name, i18n)}</h1>
            <div className={styles.productColumns}>
                <div className={styles.col0}>
                    <h1>{getPricingText(i18n.language, props.country, basePricing)}</h1>
                </div>
                <div className={styles.col1}>
                    <Form className={styles.switcher}>
                        <Form.Check
                            type="switch"
                            id={`vat-switch-${props.product.article}`}
                            onChange={(evt:any) => {props.onUiSetVat(evt.target.checked)}}
                            checked={props.vat}
                        />
                        <span>{t('editscreen.incVat')}</span>
                    </Form>
                </div>
            </div>
            <div className={styles.productColumns}>
                <div className={styles.col0}>
                    {
                        previewProduct.pricing.map((price, index) => {
                            if(index === 0) {
                                return null;
                            }
                            let finalPrice = props.vat ? applyVat(price.pricing, props.vatValue) : price.pricing;
                            return <span key={index} className={styles.productSmallGray}>{t(props.vat ? 'editscreen.pricingFrom' : 'editscreen.pricingFromNoVat', {amount: price.minAmount, price: getPricingText(i18n.language, props.country, finalPrice)})}
                                {index === previewProduct.pricing.length - 1 && 
                                    <OverlayTrigger
                                        placement={isMobile ? "bottom" : "right"}
                                        overlay={
                                            <Tooltip className={styles.tooltip}>
                                                <span dangerouslySetInnerHTML={{__html: t('editscreen.pricingInfo')}}></span>
                                            </Tooltip>
                                        }
                                        >
                                        <IconInfo/>
                                    </OverlayTrigger>
                                }
                            </span>
                       })
                    }
                    <span></span>
                </div>
                <div className={styles.col1}>
                    <span className={styles.productSmallBlack}>{translate(previewProduct.deliveryTime, i18n)}</span>
                </div>
            </div>
            <span className={styles.productMinAmount}>{t('editscreen.minAmount', {amount: getMinimumOrderAmount(previewProduct, props.library)})}</span>
            {props.product.ventilationArticle !== undefined &&
                <Form className={`${styles.switcher} ${styles.productVentilation}`}>
                    <Form.Check
                        type="switch"
                        id={`ventilation-switch-${props.product.article}`}
                        onChange={(evt:any) => {props.onChangeVentilation(!evt.target.checked)}}
                        checked={!props.useVentilation}
                    />
                    <span>{t('editscreen.excVentilation')}</span>
                </Form>
            }
            <div className={styles.productButtons}>
                <button data-testid="mymotion_orderListAddProduct" className={`${styles.buttonSub}`} disabled={isOnOrderlist} onClick={() => {
                    if(props.product.ventilationArticle !== undefined && props.useVentilation) {
                        props.onProductAdd(props.product.ventilationArticle, props.product.sizes[0])
                    } else {
                        props.onProductAdd(props.product.article, props.product.sizes[0])
                    }
                }}><IconCartBlack/>{isOnOrderlist ? t('editscreen.addToOrderListAlreadyOn') : t('editscreen.addToOrderList')}</button>
                {props.productMultiSelection && 
                    <button data-testid="mymotion_changeProduct" className={`${isSelected ? styles.buttonPrimary : styles.buttonSub} ${isSelected && styles.buttonGo}`} onClick={() => {
                        props.onUiSetConfigArticle(previewProduct.article);
                    }}>{!isSelected && <IconChange/>}{isSelected ? t('misc.continue') : t('editscreen.changeProduct')}{isSelected && <IconGo/>}</button>
                }
                {!props.productMultiSelection && 
                    <button data-testid="mymotion_changeProduct" className={`${styles.buttonSub}`} disabled={isSelected} onClick={() => {
                        props.onUiSetConfigArticle(previewProduct.article);
                    }}><IconChange/>{t('editscreen.changeProduct')}</button>
                }
            </div>
        </div>
    </div>
}


function LogoUpload(props: LogoUploadProps) {
    const {t} = useTranslation();

    const uploadInput = useRef<HTMLInputElement|null>(null);
    const [isDraggingUploadFile, setIsDraggingUploadFile] = useState(false);

    const allowedMimeTypes = ['application/postscript', 'application/pdf', 'image/png', 'image/jpeg', 'application/cdr'];

    let allowedFiles = props.allowedFileCount;
    return (
        <Fragment>
            <div className={`${styles.uploadContainer} ${isDraggingUploadFile ? styles.uploadContainerDrag : ''}`}
                onDragOver={(evt) => {
                    evt.preventDefault();
                }}
                onDragEnter={(evt) => {
                    setIsDraggingUploadFile(true);
                }}
                onDragExit={(evt) => {
                    setIsDraggingUploadFile(false);
                }}
                onDragLeave={(evt) => {
                    setIsDraggingUploadFile(false);
                }}
                onDrop={(evt) => {
                    setIsDraggingUploadFile(false);
                    evt.preventDefault();
                    if (evt.dataTransfer.items) {
                        // Use DataTransferItemList interface to access the file(s)
                        [...evt.dataTransfer.items].forEach((item, i) => {
                            // If dropped items aren't files, reject them
                            if (item.kind === 'file') {
                                const file = item.getAsFile();
                                if(allowedFiles > 0) {
                                    props.onConfigStoreLogo({'logo': file});
                                    allowedFiles -= 1;
                                }
                            }
                        });
                    } else {
                        // Use DataTransfer interface to access the file(s)
                        [...evt.dataTransfer.files].forEach((file, i) => {
                            if(allowedFiles > 0) {
                                props.onConfigStoreLogo({'logo': file});
                                allowedFiles -= 1;
                            }
                        });
                    }
                    props.onFinishedUpload();
                }}
            >
            <p>{t('editscreen.logo.upload.drag')}</p>
            <div className={styles.uploadOr}><div></div><span>{t('editscreen.logo.upload.or')}</span><div></div></div>
                <input className={styles.uploadInput} ref={uploadInput} type="file" accept={allowedMimeTypes.join(', ')} onChange={(evt) => {
                    if(evt.currentTarget.files) {
                        const files = Array.from(evt.currentTarget.files);

                        for (let index = 0; index < files.length; index++) {
                            const file = files[index];
                            if(allowedFiles > 0) {
                                props.onConfigStoreLogo({'logo': file});
                                allowedFiles -= 1;
                            }
                        }

                        evt.currentTarget.value = '';
                        props.onFinishedUpload();
                    }
                }}></input>
                <button className={`${styles.buttonBlack}`} disabled={props.ui.isStoringLogo} onClick={() => {
                    if(uploadInput.current) {
                        uploadInput.current.click();
                    }
                }}>{t('editscreen.logo.upload.browse')}</button>
                <span className={styles.logoFileFormat} dangerouslySetInnerHTML={{__html: t('editscreen.logo.upload.fileformats')}}></span>
            </div>    
        </Fragment>
    );
}

function findTagCount(products: ProductData[], tag: string): string {
    let count = 0;
    products.forEach(product => {
        if(product.tags.includes(tag) && !product.hasVentilationZipper) {
            count++;
        }
    });
    if(count === 0) {
        return '-';
    }
    return '' + count;
}

export function Editscreen(props: EditscreenProps) {
    const [isExitscreenVisible, setIsExitscreenVisible] = useState(false);
    const [isInfoscreenVisible, setIsInfoscreenVisible] = useState(false);
    
    const [isSelectingLogoProduct, setIsSelectingLogoProduct] = useState(false);
    const [isSelectingPreviewProduct1, setIsSelectingPreviewProduct1] = useState(true);
    const [isSelectingPreviewProduct2, setIsSelectingPreviewProduct2] = useState(false);    
    const [isSelectingFromProducts, setIsSelectingFromProducts] = useState(true);

    const [additionalInfoProduct, setAdditionalInfoProduct] = useState<ProductData|null>(null);

    const divRefs = [
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!)
    ];
    
    const saveDivRefs = [
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!),
        useRef<HTMLDivElement>(null!)
    ];
    const [isVentilationUsed, setIsVentilationUsed] = useState([true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true]);
    const baseEventRef = useRef<HTMLDivElement>(null!);
    const eventRef = useRef<HTMLDivElement>(null!);
    const canvasRef = useRef<HTMLCanvasElement>(null!);
    const saveCanvasRef = useRef<HTMLCanvasElement>(null!);
    const saveEventRef = useRef<HTMLDivElement>(null!);

    const [isTagMale, setIsTagMale] = useState(false);
    const [isTagFemale, setIsTagFemale] = useState(false);

    /*
    const [isTagMotion2020, setIsTagMotion2020] = useState(false);
    const [isTagMotion, setIsTagMotion] = useState(false);
    */

    const [isTagPants, setIsTagPants] = useState(false);
    const [isTagJackets, setIsTagJackets] = useState(false);

    const tabContainerRef = useRef<HTMLDivElement>(null!);
    const saveContainerRef = useRef<HTMLDivElement>(null!);

    useEffect(() => {
        if (isMobile && tabContainerRef.current) {
            tabContainerRef.current.scrollTo({top: 0, behavior: 'smooth'});
        }  
    }, [props.ui.mobileOrderStep, props.ui.editScreenTab]);
    
    const isSavingMyMotionTimeout = useRef<any>(null);
    const [isSavingMyMotion, _setIsSavingMyMotion] = useState(false);
    const setIsSavingMyMotion = (isSaving: boolean) => {
        _setIsSavingMyMotion(isSaving);  
        
        if(isSavingMyMotionTimeout.current !== null) {
            clearTimeout(isSavingMyMotionTimeout.current);
            isSavingMyMotionTimeout.current = null;
        }

        if(isSaving) {
            isSavingMyMotionTimeout.current = setTimeout(() => {
                if(saveCanvasRef.current) {
                    window.dispatchEvent(new Event('resize'));
                    saveCanvasRef.current.parentElement?.parentElement?.classList.add(styles.visible);
                }
            }, 600);
        } else {
            if(saveCanvasRef.current) {
                window.dispatchEvent(new Event('resize'));
                saveCanvasRef.current.parentElement?.parentElement?.classList.remove(styles.visible);
            }
        }
    }
    const [isCodeCopied, setIsCodeCopied] = useState(false);
    const [isLogoRightsChecked, setIsLogoRightsChecked] = useState(false);

    const [selectedLogoPlane, setSelectedLogoPlane] = useState(-1);
    const [selectedLogoSize, setSelectedLogoSize] = useState('');
    const [selectedLogoFileIndex, setSelectedLogoFileIndex] = useState(-1);
    const [selectedLogoType, _setSelectedLogoType] = useState(LogoType.Transfer);
    const setSelectedLogoType = (logoType: LogoType) => {
        if(logoType !== selectedLogoType) {
            setSelectedLogoPlane(-1);
            setSelectedLogoSize('');
            setSelectedLogoFileIndex(-1);
        }
        _setSelectedLogoType(logoType);
    };

    const [addNewLogoUpload, setAddNewLogoUpload] = useState(false);
    const [addAdditionalLogo, setAddAdditionalLogo] = useState(false);

    const {t, i18n} = useTranslation();
    
    const createDropdownPortal = isMobile ? createPortal : (content:any) => {return content;}

    const isProducts = (props.ui.editScreenTab === 'products');
    const isColors = (props.ui.editScreenTab === 'colors');
    const isLogos = (props.ui.editScreenTab === 'logos');
    const isOrderScreen = (props.ui.editScreenTab === 'orderscreen');
    const isEmpty = (props.ui.editScreenTab === '');
    
    const selectedBaseColor = props.library.baseColors.find((value) => {return value.id === props.config.baseColorId;});
    const selectedBaseColorName = selectedBaseColor ? translate(selectedBaseColor.name, i18n) : '';
    const selectedContrastColor = props.library.contrastColors.find((value) => {return value.id === props.config.contrastColorId;});
    const selectedContrastColorName = selectedContrastColor ? translate(selectedContrastColor.name, i18n) : '';
    let selectedStraussLogoName = '';
    if(props.config.straussLogo === StraussLogoType.Standard) {
        selectedStraussLogoName = t('logos.standard');
    } else if(props.config.straussLogo === StraussLogoType.StitchedBlack) {
        selectedStraussLogoName = t('logos.stitchedBlack');
    } else if(props.config.straussLogo === StraussLogoType.StitchedWhite) {
        selectedStraussLogoName = t('logos.stitchedWhite');
    }

    const basicImageLink = GetBasicImageLink(selectedBaseColor, selectedContrastColor);

    const previewProduct1 = props.library.products.find((product) => {return product.article === props.ui.previewArticle1});
    const previewProduct2 = props.library.products.find((product) => {return product.article === props.ui.previewArticle2});

    const previewProduct1IsOnOrderlist = props.config.products.some((productState, index) => {
        return (productState.article === previewProduct1?.article);
    });
    const previewProduct2IsOnOrderlist = props.config.products.some((productState, index) => {
        return (productState.article === previewProduct2?.article);
    });
    
    const filteredProducts = useMemo(() => {
        const isTagSelected = isTagMale || isTagFemale || /*isTagMotion2020 || isTagMotion ||*/ isTagPants || isTagJackets;
        const selectedTags: string[] = [];
        if(isTagMale) {
            selectedTags.push('male');
        }
        if(isTagFemale) {
            selectedTags.push('female');
        }
        /*
        if(isTagMotion2020) {
            selectedTags.push('mymotion2020');
        }
        if(isTagMotion) {
            selectedTags.push('mymotion');
        }
        */
        if(isTagPants) {
            selectedTags.push('pants');
        }
        if(isTagJackets) {
            selectedTags.push('jackets');
        }
        
        return props.library.products.filter((product) => {
            if(product.hasVentilationZipper) {
                return false;
            }

            if(!isTagSelected) {
                return true;
            }

            for (let tagIndex = 0; tagIndex < selectedTags.length; tagIndex++) {
                const selectedTag = selectedTags[tagIndex];
                if(!product.tags.includes(selectedTag)) {
                    return false;
                }
            }
            return true;
        })},
    [isTagMale, isTagFemale, isTagJackets, isTagPants, props.library.products]);
    
    const isInDetailView = (props.ui.detailArticleIndex !== 0);
    const detailProduct = (props.ui.detailArticleIndex === 1) ? previewProduct1 : previewProduct2;
    const detailProductIsOnOrderList = (props.ui.detailArticleIndex === 1) ? previewProduct1IsOnOrderlist : previewProduct2IsOnOrderlist;

    let logoArticleIndex = -1;
    const logoArticle = props.config.products.find((product, index) => {
        const matches = product.article === detailProduct?.article;
        if(matches) {
            logoArticleIndex = index;
        }
        return matches;
    });
    let previewProduct1IsInOrderlist = (logoArticle !== undefined);

    const filledLogoPlanes: number[] | undefined = logoArticle?.logos.map((logo) => {
        return logo.position;
    });

    let selectedLogoFile: LogoFile | null = (selectedLogoFileIndex >= 0 && selectedLogoFileIndex < props.config.logoFiles.length) ? props.config.logoFiles[selectedLogoFileIndex] : null;
    let currentLogoFileCount = props.config.logoFiles.length;

    const selectedLogoPlaneHasNoLogo = logoArticle === undefined || logoArticle?.logos.findIndex((logo) => { return logo.position === selectedLogoPlane; }) === -1;
    const selectedLogoPlaneData = detailProduct?.logoPlanes.find((logoPlane) => {return logoPlane.id === selectedLogoPlane;});
    const selectedLogoAreaData = props.library.logoAreas.find((logoArea) => {return logoArea.id === selectedLogoSize;});
    const hasLogosSet = logoArticle !== undefined && logoArticle?.logos.length > 0;
    const activeSelectedLogoType = hasLogosSet ? logoArticle.logos[0].logoType : selectedLogoType;
    const additionalLogosAllowed = logoArticle === undefined || logoArticle?.logos.length < 2;
    const noLogosPlacedOnArticle = logoArticle === undefined || logoArticle?.logos.length === 0;

    const additionalLogoUploadsAllowed = props.config.logoFiles.length < MAX_ALLOWED_LOGO_FILES;

    const allowedLogoPlanes: number[] | undefined = detailProduct?.logoPlanes?.filter((logoPlane) => {
        return (activeSelectedLogoType === LogoType.Stitched && logoPlane.stitchSize !== undefined) || (activeSelectedLogoType === LogoType.Transfer && logoPlane.transferSize !== undefined);
    }).map((logoPlane) => {return logoPlane.id;});

    const previewCallback1 = useRef<PreviewCallbacks>({createScreenshot: null});
    const previewCallback2 = useRef<PreviewCallbacks>({createScreenshot: null});
    const previewCallback3 = useRef<PreviewCallbacks>({createScreenshot: null});

    if(isProducts) {
        // Note(daniel): Needed to correctly align canvas views after the sidebar animation.
        setTimeout(() => {
            if(canvasRef.current) {
                window.dispatchEvent(new Event('resize'));
                canvasRef.current.parentElement?.parentElement?.classList.add(styles.visible);
            }
        }, 1100);
    }

    return (
        <Fragment>
            <div className={styles.additionalInfoContainer}>
                {additionalInfoProduct !== null &&
                    <Fragment>
                        <h1>{translate(additionalInfoProduct.name, i18n)}</h1>
                        <div className={styles.additionalInfoClose} onClick={() => {setAdditionalInfoProduct(null);}}>
                            <IconClose/>
                        </div>
                        <div className={styles.additionalInfoPanel}>
                            <iframe title={translate(additionalInfoProduct.name, i18n)} src={translate(additionalInfoProduct.articleDescriptionUrl, i18n)} frameBorder="0" scrolling="no" onLoad={(evt) => {
                                const scrollHeight = evt.currentTarget.contentWindow?.document?.body?.scrollHeight;
                                if(scrollHeight !== undefined) {
                                    evt.currentTarget.style.height=(scrollHeight+20)+'px';
                                }
                            }}></iframe>
                        </div>
                    </Fragment>
                }
            </div>
            { isInfoscreenVisible &&
                <div className={styles.mobileInfoContainer}>
                    <LogoText size={'big'} center={false} black={true}/>
                    {isMobile && <div className={styles.mobileInfoClose} onClick={() => {
                        setIsInfoscreenVisible(false);
                    }}><IconClose/></div>}
                    <LanguageSwitcherContainer lineBreak/>
                    <LinkFooterContainer/>          
                    <hr></hr> 
                    <h1>{t('mobile.info.header')}</h1>
                    <div className={styles.mobileInfoTextContainer}>
                        <IconPhone/>
                        <p dangerouslySetInnerHTML={{__html: t('mobile.info.text')}}></p>
                    </div>
                </div>
            }
            <Container fluid className={`${styles.container} ${additionalInfoProduct !== null ? styles.additionalInfoScreenVisible : ''}`} ref={baseEventRef}>
                {
                    props.library.baseColors.map((color, index) => {
                        return <div key={index} className={styles.background} style={{background: isOrderScreen ? '#F0F1F1' : generateBackgroundStyle(color.bgColor, isMobile), opacity: color.id === props.config.baseColorId ? 1.0 : 0.0}}></div>
                    })
                }
                {isExitscreenVisible && <Exitscreen code={props.config.code} onCloseExitscreen={() => {setIsExitscreenVisible(false);}} onQuit={() => {props.onUiGoTo('splashscreen')}}/>}
                <div className={styles.header}>
                    {isMobile && (isProducts || isColors || isLogos) && <div className={styles.mobileBack} onClick={() => {
                        props.onUiSetEditScreenTab('')
                    }}><IconArrowLeft/></div>}

                    <LogoText size={'big'} center={isMobile && !isOrderScreen && !isEmpty} black={(isMobile && isOrderScreen) || (!isMobile && isProducts)}/>
                    {isMobile && <div className={styles.mobileInfo} onClick={() => {
                        setIsInfoscreenVisible(true);
                    }}>{isOrderScreen && <IconInfoBlack/>}{!isOrderScreen && <IconInfoWhite/>}</div>}
                </div>
                <div className={`${styles.content} ${(isLogos || isInDetailView) ? styles.contentLogos : ''} ${isEmpty ? styles.contentEmpty : ''}`}>
                    {(isColors || isEmpty) && !isInDetailView &&
                        <Fragment>
                            <div className={`${styles.contentLeft} ${isColors ? styles.contentLeftColors : ''}`}>
                                {previewProduct1 !== undefined &&
                                    <Fragment>
                                        <div className={`${styles.previewContainer}`}>
                                            <BackgroundSprite1 className={styles.backgroundSplash} fill={generateBackgroundSplashContrastFill(selectedContrastColor)} />
                                            <Preview library={props.library} showDetails={isMobile && isEmpty} model={previewProduct1.article} modelData={previewProduct1.modelData} xOffset={0} yOffset={isMobile ? 30 : -50} cameraScale={1.5} baseColorId={props.config.baseColorId} contrastColorId={props.config.contrastColorId} straussLogo={props.config.straussLogo} showPlanes={false} callbacks={previewCallback1} />
                                        
                                            <DropdownButton containerColor={selectedBaseColor?.bgColor} centered={isEmpty}>
                                                {window.SALES && <button className={`${styles.buttonWhite}`} onClick={(evt) => {
                                                    if(previewCallback1.current && previewCallback1.current.createScreenshot) {
                                                        previewCallback1.current.createScreenshot();
                                                    }
                                                }}>{t('misc.capture')}</button>}
                                                <button data-testid="mymotion_dropdownChangeProduct" className={`${styles.buttonWhite}`} onClick={(evt) => {
                                                    setIsSelectingLogoProduct(false); 
                                                    setIsSelectingFromProducts(false); 
                                                    setIsSelectingPreviewProduct1(true); 
                                                    setIsSelectingPreviewProduct2(false); 
                                                    props.onUiSetEditScreenTab('products');
                                                }}><IconChange/>{t('editscreen.changeProduct')}</button>
                                                {!isMobile && <button data-testid="mymotion_dropdownProductDetails" className={`${styles.buttonWhite}`} onClick={(evt) => {props.onUiSetDetailArticleIndex(1); }}><IconInfoBlack/>{t('editscreen.productDetails')}</button>}
                                                <button data-testid="mymotion_dropdownPersonalize" className={`${styles.buttonWhite}`} onClick={(evt) => {props.onUiSetDetailArticleIndex(1); props.onUiSetEditScreenTab('logos');}}><IconShirt/>{t('editscreen.personalize')}</button>
                                                <button data-testid="mymotion_dropdownOrderListAddProduct" className={`${styles.buttonWhite}`} disabled={previewProduct1IsOnOrderlist} onClick={(evt) => {if(!previewProduct1IsOnOrderlist) {props.onProductAdd(previewProduct1.article, previewProduct1.sizes[0]);}} }><IconCartBlack/>{previewProduct1IsOnOrderlist ? t('editscreen.addToOrderListAlreadyOn') : t('editscreen.addToOrderList')}</button>
                                            </DropdownButton>
                                        </div>
                                    </Fragment>
                                }
                            </div>
                            <div className={styles.contentRight}>
                                {previewProduct2 !== undefined &&
                                    <Fragment>
                                        <div className={styles.previewContainer}>                                            
                                            <BackgroundSprite2 className={styles.backgroundSplash} fill={generateBackgroundSplashBaseFill(selectedBaseColor)} />
                                            <Preview library={props.library} model={previewProduct2.article} isRight modelData={previewProduct2.modelData} xOffset={0} yOffset={120} cameraScale={1.5} baseColorId={props.config.baseColorId} contrastColorId={props.config.contrastColorId} straussLogo={props.config.straussLogo} showPlanes={false} callbacks={previewCallback2} />
                                            <DropdownButton containerColor={selectedBaseColor?.bgColor} downward={true}>
                                                {window.SALES && <button className={`${styles.buttonWhite}`} onClick={(evt) => {
                                                    if(previewCallback2.current && previewCallback2.current.createScreenshot) {
                                                        previewCallback2.current.createScreenshot();
                                                    }
                                                }}>{t('misc.capture')}</button>}
                                                <button data-testid="mymotion_dropdownChangeProduct" className={`${styles.buttonWhite}`} onClick={(evt) => {
                                                    setIsSelectingLogoProduct(false); 
                                                    setIsSelectingFromProducts(false); 
                                                    setIsSelectingPreviewProduct1(false); 
                                                    setIsSelectingPreviewProduct2(true); 
                                                    props.onUiSetEditScreenTab('products');
                                                }}><IconChange/>{t('editscreen.changeProduct')}</button>
                                                {!isMobile && <button data-testid="mymotion_dropdownProductDetails" className={`${styles.buttonWhite}`} onClick={(evt) => {props.onUiSetDetailArticleIndex(2); }}><IconInfoBlack/>{t('editscreen.productDetails')}</button>}
                                                <button data-testid="mymotion_dropdownPersonalize" className={`${styles.buttonWhite}`} onClick={(evt) => {props.onUiSetDetailArticleIndex(2); props.onUiSetEditScreenTab('logos');}}><IconShirt/>{t('editscreen.personalize')}</button>
                                                <button data-testid="mymotion_dropdownOrderListAddProduct" className={`${styles.buttonWhite}`} disabled={previewProduct2IsOnOrderlist} onClick={(evt) => {if(!previewProduct2IsOnOrderlist) { props.onProductAdd(previewProduct2.article, previewProduct2.sizes[0]);}}}><IconCartBlack/>{previewProduct2IsOnOrderlist ? t('editscreen.addToOrderListAlreadyOn') : t('editscreen.addToOrderList')}</button>
                                            </DropdownButton>
                                        </div>
                                    </Fragment>
                                }
                                <div className={styles.contentBasics}>
                                    {basicImageLink !== null && <Fragment>
                                        <div className={styles.contentBasicsImageContainer}>
                                            <img alt={t('editscreen.basics') as string} src={basicImageLink}></img>
                                        </div>
                                        <a data-testid="mymotion_basics" className={`${styles.buttonBlack}`} href={GetBasicsLink(i18n.language, selectedContrastColor)} rel="noreferrer" target="_blank">{t('editscreen.basics')}</a>
                                    </Fragment>}
                                </div>
                            </div>
                        </Fragment>
                    }
                    {isInDetailView && detailProduct !== undefined && !isOrderScreen &&
                        <Fragment>
                            <div className={styles.contentLeftCenter}>
                                {!isMobile && <button 
                                    className={`${styles.buttonSecondary} ${styles.backToOverviewButton}`}
                                    onClick={() => {
                                        props.onUiSetDetailArticleIndex(0);
                                        if(props.ui.editScreenTab === 'logos') {
                                            props.onUiSetEditScreenTab('colors');
                                        }
                                        if(additionalInfoProduct !== null) {
                                            setAdditionalInfoProduct(null);
                                        }
                                    }}
                                >
                                    <IconArrowLeft/>{t('misc.back')}
                                </button>}
                                <h1>{detailProduct ? translate(detailProduct.name, i18n) : 'NOT FOUND'}</h1>
                                {
                                    detailProduct !== undefined &&
                                    <div>
                                        {!isLogos && !isMobile &&
                                            <Fragment>
                                                {additionalInfoProduct === null && <button 
                                                    className={`${styles.buttonWhite} ${styles.showAdditionalInfoButton}`}
                                                    onClick={() => {
                                                        setAdditionalInfoProduct(detailProduct);
                                                    }}
                                                ><IconPlus/>{t('editscreen.showAdditionalInfo')}</button>}
                                                {additionalInfoProduct !== null && <button 
                                                    className={`${styles.buttonBlack} ${styles.closeAdditionalInfoButton}`}
                                                    onClick={() => {
                                                        setAdditionalInfoProduct(null);
                                                    }}
                                                ><IconClose/>{t('editscreen.closeAdditionalInfo')}</button>}
                                            </Fragment>
                                        }
                                        <DropdownButton containerColor={selectedBaseColor?.bgColor} centered={isMobile && isEmpty}>
                                            {window.SALES && <button className={`${styles.buttonWhite}`} onClick={(evt) => {
                                                console.log(previewCallback3)
                                                    if(previewCallback3.current && previewCallback3.current.createScreenshot) {
                                                        previewCallback3.current.createScreenshot();
                                                    }
                                                }}>{t('misc.capture')}</button>}
                                            <button data-testid="mymotion_dropdownChangeProduct" className={`${styles.buttonWhite}`} onClick={(evt) => {
                                                setIsSelectingLogoProduct(isLogos);
                                                setIsSelectingFromProducts(false); 
                                                setIsSelectingPreviewProduct1(props.ui.detailArticleIndex === 1);
                                                setIsSelectingPreviewProduct2(props.ui.detailArticleIndex === 2);
                                                props.onUiSetEditScreenTab('products');
                                                setAdditionalInfoProduct(null);
                                            }}><IconChange/>{t('editscreen.changeProduct')}</button>
                                            {
                                                (detailProduct !== undefined && (detailProduct.ventilationArticle !== undefined || detailProduct.hasVentilationZipper)) && 
                                                    <button className={`${styles.buttonWhite}`} onClick={(evt) => {
                                                        if(detailProduct.ventilationArticle !== undefined) {
                                                            if(previewProduct1?.article === detailProduct.article) {
                                                                props.onUiSetPreviewArticle1(detailProduct.ventilationArticle);
                                                            } else {
                                                                props.onUiSetPreviewArticle2(detailProduct.ventilationArticle);
                                                            }
                                                        } 
                                                        if(detailProduct.nonVentilationArticle !== undefined) {
                                                            if(previewProduct1?.article === detailProduct.article) {
                                                                props.onUiSetPreviewArticle1(detailProduct.nonVentilationArticle);
                                                            } else {
                                                                props.onUiSetPreviewArticle2(detailProduct.nonVentilationArticle);
                                                            }
                                                        }
                                                    }}>
                                                        <div className={`${styles.ventilationInfoSwitcher} ${detailProduct.hasVentilationZipper ? styles.ventilationInfoSwitcherOn : ''}`}><div></div></div>
                                                        {detailProduct.hasVentilationZipper && t('editscreen.invVentilationShort')}
                                                        {!detailProduct.hasVentilationZipper && t('editscreen.excVentilationShort')}
                                                        <OverlayTrigger
                                                            placement="right"
                                                            overlay={
                                                                <Tooltip className={styles.tooltip}>
                                                                    <span dangerouslySetInnerHTML={{__html: t('editscreen.ventilationInfo')}}></span>
                                                                </Tooltip>
                                                            }
                                                            >
                                                            <IconInfoBlack className={styles.tooltipInfoRight}/>
                                                        </OverlayTrigger>
                                                    </button>
                                            }
                                            {(isLogos || isMobile) && <button data-testid="mymotion_dropdownProductDetails" className={`${styles.buttonWhite}`} onClick={(evt) => {setAdditionalInfoProduct(detailProduct);}}><IconInfoBlack/>{t('editscreen.productDetails')}</button>}
                                            <button data-testid="mymotion_dropdownOrderListAddProduct" className={`${styles.buttonWhite}`} disabled={detailProductIsOnOrderList} onClick={(evt) => {if(!detailProductIsOnOrderList) { props.onProductAdd(detailProduct.article, detailProduct.sizes[0])}}}><IconCartBlack/>{detailProductIsOnOrderList ? t('editscreen.addToOrderListAlreadyOn') : t('editscreen.addToOrderList')}</button>
                                        </DropdownButton>
                                    </div>
                                }
                            </div>
                            <div className={styles.contentRight}>
                                {
                                    detailProduct !== undefined &&
                                    <div className={styles.previewContainerHigh}>
                                        <BackgroundSprite1 className={styles.backgroundSplash} fill={generateBackgroundSplashContrastFill(selectedContrastColor)} />
                                        <Preview 
                                            library={props.library}
                                            model={detailProduct.article}
                                            modelData={detailProduct.modelData} 
                                            xOffset={0}
                                            cameraScale={1.3}
                                            baseColorId={props.config.baseColorId}
                                            contrastColorId={props.config.contrastColorId}
                                            straussLogo={props.config.straussLogo}
                                            showPlanes={isLogos}
                                            filledPlanes={filledLogoPlanes}
                                            selectedPlanes={additionalLogosAllowed ? [selectedLogoPlane] : []}
                                            allowedPlanes={allowedLogoPlanes}
                                            showDetails={!isLogos && (!isMobile || isEmpty)}
                                            isRight
                                            callbacks={previewCallback3}
                                            onPlaneSelected={(additionalLogosAllowed && (logoArticle === undefined || noLogosPlacedOnArticle || addAdditionalLogo)) ? (planeId) => {
                                                if(selectedLogoPlane === planeId) {
                                                    setSelectedLogoPlane(-1);
                                                } else {
                                                    setSelectedLogoPlane(planeId);
                                                }
                                            } : undefined}
                                        />
                                    </div>
                                }
                            </div>
                        </Fragment>
                    }
                </div>  
                <div className={`${styles.panelRight} ${isProducts ? styles.panelRightExtended : ''} ${isColors ? styles.panelRightColors : ''} ${isLogos ? styles.panelRightLogos : ''} ${isOrderScreen ? styles.panelRightOrderscreen : ''}`}>
                    <div className={styles.panelRightCircle}>
                        
                    </div>             
                    <div className={styles.panelRightContainer}>
                        <div className={styles.buttonContainer}>
                            {isColors && <button data-testid="mymotion_inspire" className={`${styles.buttonBlack}`} onClick={() => {props.onUiGoTo('inspiration')}}>{t('editscreen.inspire')}</button>}
                            <button data-testid="mymotion_orderList" className={`${styles.buttonPrimary}`} onClick={() => {props.onUiSetDetailArticleIndex(0);props.onUiSetEditScreenTab('colors'); props.onUiGoTo('orderscreen')}}><IconCart/>{t('editscreen.orderlist')}
                                {props.config.products.length > 0 &&
                                    <span className={styles.orderCount}>{props.config.products.length}</span>
                                }
                            </button>
                        </div>
                        <div className={styles.tabList}>
                            <button 
                                data-testid="mymotion_chooseProductsTop"
                                className={`${styles.tabButton} ${isProducts ? styles.tabButtonActive : ''}`} 
                                onClick={() => {
                                    setIsSelectingLogoProduct(false); 
                                    setIsSelectingPreviewProduct1(true); 
                                    setIsSelectingPreviewProduct2(false); 
                                    props.onUiSetEditScreenTab('products');
                                    setIsSelectingFromProducts(true); 
                                }}><IconChange/>{t('editscreen.products')}</button>
                            {isProducts && <div className={styles.tabProductTags}>
                                <div className={isTagMale ? styles.tagSelected : ''} onClick={() => {setIsTagMale(!isTagMale); if(!isTagMale) {setIsTagFemale(false);} }}><span>{t('gender.male')}</span><span>{findTagCount(filteredProducts, 'male')}</span></div>
                                <div className={isTagFemale ? styles.tagSelected : ''} onClick={() => {setIsTagFemale(!isTagFemale); if(!isTagFemale) {setIsTagMale(false);}}}><span>{t('gender.female')}</span><span>{findTagCount(filteredProducts, 'female')}</span></div>
                                {
                                /*
                                <div className={styles.tagSeperator}></div>
                                <div className={isTagMotion2020 ? styles.tagSelected : ''} onClick={() => {setIsTagMotion2020(!isTagMotion2020);}}><span>{t('tags.motion2020')}</span><span>{findTagCount(props.library.products, 'mymotion2020')}</span></div>
                                <div className={isTagMotion ? styles.tagSelected : ''} onClick={() => {setIsTagMotion(!isTagMotion);}}><span>{t('tags.motion')}</span><span>{findTagCount(props.library.products, 'mymotion')}</span></div>
                                */
                                }
                                <div className={styles.tagSeperator}></div>
                                <div className={`${isTagPants ? styles.tagSelected : ''}`} onClick={() => {setIsTagPants(!isTagPants); if(!isTagPants) {setIsTagJackets(false);}}}><span>{t('tags.pants')}</span><span>{findTagCount(filteredProducts, 'pants')}</span></div>
                                <div className={`${isTagJackets ? styles.tagSelected : ''}`} onClick={() => {setIsTagJackets(!isTagJackets); if(!isTagJackets) {setIsTagPants(false);}}}><span>{t('tags.jackets')}</span><span>{findTagCount(filteredProducts, 'jackets')}</span></div>
                            </div>}     
                            <button
                                data-testid="mymotion_chooseColours"
                                className={`${styles.tabButton} ${isColors ? styles.tabButtonActive : ''}`}
                                onClick={() => {
                                    props.onUiSetDetailArticleIndex(0);
                                    props.onUiSetEditScreenTab('colors');
                                    setIsSelectingFromProducts(false); 
                                }}>
                                    <div className={styles.colorIcon}></div>{t('editscreen.colors')}
                            </button>
                            <button data-testid="mymotion_chooseLogo" className={`${styles.tabButton} ${isLogos ? styles.tabButtonActive : ''}`}
                                onClick={() => {
                                    if(props.ui.detailArticleIndex === 0) {
                                        props.onUiSetDetailArticleIndex(1);
                                    };
                                    props.onUiSetEditScreenTab('logos');
                                    setIsSelectingFromProducts(false); 
                                }}><IconShirt/>{t('editscreen.logos')}
                            </button>
                        </div>
                        <div className={styles.tabContainer} ref={tabContainerRef}>
                           {isColors &&
                                <div className={styles.tabContent}>
                                    <h1><span>{t('editscreen.baseColor')}</span><span className={styles.headerInfo}>{t('editscreen.selection')}: {selectedBaseColorName}</span></h1>
                                    <div className={styles.colorSwatchContainer}>
                                        {
                                            props.library.baseColors.map((color, index) => {
                                                return <button data-testid="mymotion_colorSwatch" key={index} disabled={!IsBaseColorAllowed(color, selectedContrastColor)} className={`${styles.colorSwatch} ${props.config.baseColorId === color.id ? styles.colorSwatchActive : ''} ${color.id === '06' ? styles.colorSwatchBorder : ''}`} style={{backgroundColor: color.color}} onClick={() => {props.onConfigSetBaseColor(color.id)}}></button>;
                                            })
                                        }
                                    </div>
                                    <h1><span>{t('editscreen.contrastColor')}</span><span className={styles.headerInfo}>{t('editscreen.selection')}: {selectedContrastColorName}</span></h1>
                                    <div className={styles.colorSwatchContainer}>
                                        {
                                            props.library.contrastColors.map((color, index) => {
                                                return <button data-testid="mymotion_colorSwatch" key={index} disabled={!IsContrastColorAllowed(color, selectedBaseColor)} className={`${styles.colorSwatch} ${props.config.contrastColorId === color.id ? styles.colorSwatchActive : ''}`} style={{backgroundColor: color.color}} onClick={() => {props.onConfigSetContrastColor(color.id)}}></button>;
                                            })
                                        }
                                    </div>
                                    <h1><span>{t('editscreen.straussLogo')}</span><span className={styles.headerInfo}>{t('editscreen.selection')}: {selectedStraussLogoName}</span></h1>
                                    <div className={styles.logoContainer}>
                                        <div data-testid="mymotion_logoSwatch" className={styles.logoButton} onClick={() => {props.onConfigSetStraussLogo(StraussLogoType.Standard)}}>
                                            <IconStandard/>
                                            <span>
                                                {t('logos.standard')}
                                            </span>
                                            <div className={styles.logoSelector}>
                                                {props.config.straussLogo === StraussLogoType.Standard && <div className={styles.logoSelectorSelected}></div>}
                                            </div>
                                        </div>
                                        <div data-testid="mymotion_logoSwatch" className={styles.logoButton} onClick={() => {props.onConfigSetStraussLogo(StraussLogoType.StitchedBlack)}}>
                                            <IconBlack/>
                                            <span>
                                                {t('logos.stitchedBlack')}
                                            </span>
                                            <div className={styles.logoSelector}>
                                                {props.config.straussLogo === StraussLogoType.StitchedBlack && <div className={styles.logoSelectorSelected}></div>}
                                            </div>
                                        </div>
                                        <div data-testid="mymotion_logoSwatch" className={styles.logoButton} onClick={() => {props.onConfigSetStraussLogo(StraussLogoType.StitchedWhite)}}>
                                            <IconWhite/>
                                            <span>
                                                {t('logos.stitchedWhite')}
                                            </span>
                                            <div className={styles.logoSelector}>
                                                {props.config.straussLogo === StraussLogoType.StitchedWhite && <div className={styles.logoSelectorSelected}></div>}
                                            </div>
                                        </div>
                                    </div>
                                    {isMobile &&                            
                                        <Fragment>
                                            <button data-testid="mymotion_inspire" className={`${styles.buttonSecondary} ${styles.buttonMobileInspire}`} onClick={() => {props.onUiGoTo('inspiration')}}>{t('editscreen.inspire')}</button>
                                            {!previewProduct1IsInOrderlist && previewProduct1 && <button className={`${styles.buttonPrimary} ${styles.buttonMobileAddToOrderlist}`} onClick={() => {
                                                    props.onProductAdd(previewProduct1.article, previewProduct1.sizes[0])
                                                }}><IconCart/>{t('editscreen.addToOrderList')}</button>
                                            }                                            
                                            <button data-testid="mymotion_save" className={`${styles.buttonSecondary} ${styles.buttonMobileSave}`} onClick={() => {setIsSavingMyMotion(true)}}>{t('editscreen.save')}</button>
                                        </Fragment>
                                    }
                                </div>
                            }
                            {isProducts &&
                                <div className={styles.tabContent} ref={eventRef}>
                                    <PreviewContainer
                                        className={styles.fullCanvas}
                                        eventRef={eventRef}
                                        scrollRef={tabContainerRef}
                                        canvasRef={canvasRef}
                                    >
                                        {
                                            filteredProducts.map((product, index) => {
                                                let ventilationProduct:ProductData|undefined = undefined;
                                                if (product.ventilationArticle !== undefined) {
                                                    ventilationProduct = props.library.products.find((ventProduct) => {return ventProduct.article === product.ventilationArticle});
                                                }
                                                const useVentilation = isVentilationUsed[index];
                                                let productData = ventilationProduct !== undefined && useVentilation ? ventilationProduct : product;
                                                return <PreviewContainerModel key={product.article + index.toString()} divRef={divRefs[index]} library={props.library} model={productData.article} thumbnail={true} modelData={productData.modelDataThumbnail !== undefined ? productData.modelDataThumbnail : productData.modelData} baseColorId={props.config.baseColorId} contrastColorId={props.config.contrastColorId} straussLogo={props.config.straussLogo} waitBeforeShow={isMobile ? 0 : Math.random() * 500} showPlanes={false} xOffset={0}/>;
                                            })
                                        }
                                    </PreviewContainer>
                                    <div className={styles.productContainer}>
                                        { filteredProducts.map((product, index) => {
                                                let ventilationProduct:ProductData|undefined = undefined;
                                                if (product.ventilationArticle !== undefined) {
                                                    ventilationProduct = props.library.products.find((ventProduct) => {return ventProduct.article === product.ventilationArticle});
                                                }
                                                const selectedArticles:string[] = [];
                                                if(isSelectingFromProducts) {
                                                    selectedArticles.push(props.ui.previewArticle1);
                                                    if(!isMobile) {
                                                        selectedArticles.push(props.ui.previewArticle2);
                                                    }
                                                } else {
                                                    if (!isSelectingLogoProduct) {
                                                        if(isSelectingPreviewProduct1 && !isMobile) {
                                                            selectedArticles.push(props.ui.previewArticle2);
                                                        }
                                                        if(isSelectingPreviewProduct2) {
                                                            selectedArticles.push(props.ui.previewArticle1);
                                                        }
                                                    }
                                                }
                                                return <Product key={product.article} index={index} library={props.library} config={props.config} product={product} ventilationProduct={ventilationProduct} useVentilation={isVentilationUsed[index]} country={props.ui.country} vat={props.ui.vat} vatValue={props.ui.country.vat} selectedArticles={selectedArticles} productMultiSelection={isSelectingFromProducts}
                                                    onChangeVentilation={(ventilation:boolean) => {
                                                        let newVentilationArray = [...isVentilationUsed];
                                                        newVentilationArray[index] = ventilation;
                                                        setIsVentilationUsed(newVentilationArray);
                                                    }} 
                                                    onProductAdd={props.onProductAdd}
                                                    onUiSetConfigArticle={
                                                        (article: string) => {
                                                            if(isSelectingLogoProduct) {
                                                                if(props.ui.detailArticleIndex === 1) {
                                                                    props.onUiSetPreviewArticle1(article);
                                                                } else if(props.ui.detailArticleIndex === 2) {
                                                                    props.onUiSetPreviewArticle2(article);
                                                                }
                                                                setIsSelectingLogoProduct(false);
                                                                props.onUiSetEditScreenTab('logos');
                                                            } else if (isSelectingFromProducts) {
                                                                if(previewProduct1?.article === article || previewProduct2?.article === article) {
                                                                    setIsSelectingFromProducts(false);
                                                                    props.onUiSetDetailArticleIndex(0);
                                                                    props.onUiSetEditScreenTab('colors');
                                                                }
                                                                const newProduct = props.library.products.find((product) => {return product.article === article});
                                    
                                                                if(newProduct?.tags.includes('pants')) {
                                                                    props.onUiSetPreviewArticle1(article);
                                                                } else if(newProduct?.tags.includes('jackets')) {
                                                                    props.onUiSetPreviewArticle2(article);
                                                                }
                                                            } else if(isSelectingPreviewProduct1) {
                                                                props.onUiSetPreviewArticle1(article);
                                                                setIsSelectingPreviewProduct1(false);
                                                                props.onUiSetEditScreenTab('colors');
                                                            } else if(isSelectingPreviewProduct2) {
                                                                props.onUiSetPreviewArticle2(article);
                                                                setIsSelectingPreviewProduct2(false);
                                                                props.onUiSetEditScreenTab('colors');
                                                            } else {
                                                                console.log("Shouldn't happen!");
                                                            }
                                                        }
                                                    }
                                                    onUiSetVat={props.onUiSetVat}
                                                    divRef={divRefs[index]}
                                                ></Product>;
                                            })
                                        }
                                    </div>
                                    {isMobile &&                                                       
                                        <button data-testid="mymotion_save" className={`${styles.buttonSecondary} ${styles.buttonMobileSave}`} onClick={() => {setIsSavingMyMotion(true)}}>{t('editscreen.save')}</button>
                                    }
                                </div>
                            }
                            {isLogos &&
                                <div className={styles.tabContent}>
                                    <div className={styles.logoContent}>
                                        <h1>{t('editscreen.logo.header')}</h1>
                                        <p>{t('editscreen.logo.intro')}</p>
                                        <div className={styles.logotypeContainer}>
                                            <div className={`${styles.logoButton} ${hasLogosSet ? styles.logoButtonDisabled : ''}`} onClick={() => {if(!hasLogosSet) { setSelectedLogoType(LogoType.Transfer); }}}>
                                                <div className={styles.logoSelector}>
                                                    {activeSelectedLogoType === LogoType.Transfer && <div className={styles.logoSelectorSelected}></div>}
                                                </div>
                                                <IconTransfer/>
                                                <span>
                                                    {t('editscreen.logo.type.print')}
                                                </span>
                                                {isMobile && <span dangerouslySetInnerHTML={{__html: t('editscreen.logo.transferInfo')}}></span>}
                                                {!isMobile && <OverlayTrigger
                                                        placement="bottom"
                                                        overlay={
                                                            <Tooltip className={styles.tooltip}>
                                                                <span dangerouslySetInnerHTML={{__html: t('editscreen.logo.transferInfo')}}></span>
                                                            </Tooltip>
                                                        }
                                                        >
                                                        <IconInfo/>
                                                    </OverlayTrigger>
                                                }
                                            </div>
                                            <div className={`${styles.logoButton} ${hasLogosSet ? styles.logoButtonDisabled : ''}`} onClick={() => {if(!hasLogosSet) {setSelectedLogoType(LogoType.Stitched);}}}>
                                                <div className={styles.logoSelector}>
                                                    {activeSelectedLogoType === LogoType.Stitched && <div className={styles.logoSelectorSelected}></div>}
                                                </div>
                                                <IconDirect/>
                                                <span>
                                                    {t('editscreen.logo.type.stitch')}
                                                </span>
                                                {isMobile && <span dangerouslySetInnerHTML={{__html: t('editscreen.logo.directInfo')}}></span>}
                                                {!isMobile && <OverlayTrigger
                                                        placement="bottom"
                                                        overlay={
                                                            <Tooltip className={styles.tooltip}>
                                                                <span dangerouslySetInnerHTML={{__html: t('editscreen.logo.directInfo')}}></span>
                                                            </Tooltip>
                                                        }
                                                        >
                                                        <IconInfo/>
                                                    </OverlayTrigger>
                                                }
                                            </div>
                                        </div>
                                        <p><span className={styles.infoBold}>{t('editscreen.logo.info')}: </span>{t('editscreen.logo.infoText')}</p>
                                        {
                                            <div className={styles.logoUploadContainer}>
                                                <h2>{t('editscreen.logo.upload.header')}</h2>
                                                <p>{t('editscreen.logo.upload.info')}</p>
                                                <div className={styles.logoSelectionList}>
                                                    {
                                                        props.config.logoFiles.map((logo, logoIndex) => {
                                                            return <div 
                                                                    className={`${styles.logoSelectionEntry} ${styles.logoSelectionEntryWhite}`} 
                                                                    key={logoIndex}
                                                                    onClick={() => {
                                                                        setSelectedLogoFileIndex(logoIndex);
                                                                    }}
                                                                >
                                                                {(logo.originalFilename.endsWith('.png') || logo.originalFilename.endsWith('.jpg') || logo.originalFilename.endsWith('.jpeg')) &&
                                                                    <div className={styles.logoListEntryImage}>
                                                                        <img src={getURL(GET_LOGO, {
                                                                            id: props.config.code,
                                                                            filename: logo.filename
                                                                        })} alt={logo.filename}></img>
                                                                    </div>
                                                                }

                                                                <div>
                                                                    <p>{logo.originalFilename}</p>
                                                                    <p className={styles.logoListEntrySize}>{logo.size.toFixed(2)} MB</p>
                                                                </div>
                                                                <button className={styles.logoListRemove} onClick={(evt) => {
                                                                    setSelectedLogoFileIndex(-1);
                                                                    props.onConfigDeleteLogo(logo.filename);
                                                                    evt.stopPropagation();
                                                                }}><IconTrash/></button>
                                                            </div>;
                                                        })
                                                    }
                                                    {!addNewLogoUpload && additionalLogoUploadsAllowed &&
                                                        <div 
                                                            className={`${styles.buttonPrimary}`} 
                                                            onClick={() => {
                                                                setAddNewLogoUpload(true);
                                                            }}>
                                                            <IconPlus/>{props.config.logoFiles.length === 0 ? t('editscreen.logo.uploadFirst') : t('editscreen.logo.uploadSecond')}
                                                        </div>
                                                    }
                                                </div>
                                                {(additionalLogoUploadsAllowed && addNewLogoUpload) &&
                                                    <Fragment>
                                                        <Form className={`${styles.switcher} ${styles.switcherLogoRights}`}>
                                                        <Form.Check
                                                            type="switch"
                                                            id="logo-switch"
                                                            onChange={(evt:any) => {setIsLogoRightsChecked(evt.target.checked)}}
                                                            checked={isLogoRightsChecked}
                                                        />
                                                        <p dangerouslySetInnerHTML={{__html: t('editscreen.logo.upload.rights')}}></p>
                                                        </Form>
                                                        {isLogoRightsChecked &&
                                                            <Fragment>
                                                                <LogoUpload ui={props.ui} allowedFileCount={MAX_ALLOWED_LOGO_FILES - props.config.logoFiles.length} onConfigStoreLogo={props.onConfigStoreLogo} onFinishedUpload={()=>{
                                                                    setAddNewLogoUpload(false);
                                                                    setSelectedLogoFileIndex(currentLogoFileCount);
                                                                }}/>
                                                            </Fragment>
                                                        }
                                                    </Fragment>
                                                }
                                            </div>
                                        }
                                        {
                                            (hasLogosSet && detailProduct !== undefined) && 
                                            <Fragment>
                                                <h1>{t('editscreen.logo.existing')}</h1>
                                                <div className={styles.logoList}>
                                                    {
                                                        (logoArticle !== undefined) && logoArticle.logos.map((logo, logoIndex) => {
                                                            let logoPlacementText = detailProduct.logoPlanes.find((plane) => {
                                                                return plane.id === logo.position;
                                                            }) 
                                                            let logoFile = props.config.logoFiles.find((file) => {
                                                                return file.filename === logo.filename;
                                                            })
                                                            return <div className={styles.logoListEntry} key={logoIndex}>
                                                                {logoFile !== undefined && (logoFile.originalFilename.endsWith('.png') || logoFile.originalFilename.endsWith('.jpg') || logoFile.originalFilename.endsWith('.jpeg')) &&
                                                                    <div className={styles.logoListEntryImage}>
                                                                        <img src={getURL(GET_LOGO, {
                                                                            id: props.config.code,
                                                                            filename: logoFile.filename
                                                                        })} alt={logoFile.filename}></img>
                                                                    </div>
                                                                }
                                                                <div className={styles.logoListEntryText}>                                                                    
                                                                    <p className={styles.logoListEntryHeader}>{logo.logoType === LogoType.Stitched ? t('editscreen.logo.type.stitch') : t('editscreen.logo.type.print')} | Logo {logoIndex + 1}</p>    
                                                                    <p>{t('editscreen.logo.placement')}: {logoPlacementText !== undefined ? translate(logoPlacementText.name, i18n) : 'NOT FOUND'} ({logo.position})</p>
                                                                    {logoFile !== undefined && 
                                                                        <Fragment>
                                                                            <p>{logoFile.originalFilename}</p>                                                            
                                                                            <p className={styles.logoListEntrySize}>{logoFile.size.toFixed(2)} MB</p>
                                                                        </Fragment>
                                                                    }
                                                                </div>
                                                                <button className={styles.logoListRemove} onClick={() => {
                                                                    let updatedProduct: ProductState = Object.assign({}, logoArticle);
                                                                    const logoIndex = updatedProduct.logos.findIndex((logoEntry) => {
                                                                        return logoEntry.position === logo.position;
                                                                    });
                                                                    if(logoIndex !== -1) {
                                                                        updatedProduct.logos.splice(logoIndex, 1);
                                                                    }
                                                                    props.onProductUpdate(logoArticleIndex, updatedProduct);
                                                                }}><IconTrash/></button>
                                                            </div>;
                                                        })
                                                    }
                                                    {!addAdditionalLogo && additionalLogosAllowed &&
                                                        <div 
                                                            className={styles.buttonPrimary} 
                                                            onClick={() => {
                                                                setAddAdditionalLogo(true);
                                                            }}>
                                                            <IconPlus/>{t('editscreen.logo.addAdditional')}
                                                        </div>
                                                    }
                                                </div>
                                            </Fragment>
                                        }
                                        {
                                             ((logoArticle === undefined || (selectedLogoPlaneHasNoLogo && additionalLogosAllowed))) &&
                                             (noLogosPlacedOnArticle || addAdditionalLogo) &&
                                             <Fragment>
                                                <h1>{noLogosPlacedOnArticle ? t('editscreen.logo.add') : t('editscreen.logo.addAdditional')}</h1>
                                                {
                                                    (selectedLogoPlane === -1) &&
                                                    <Fragment>
                                                        <p>{t('editscreen.logo.placementHelp')}</p>
                                                        <div className={styles.logoPlacementInfo}>
                                                            <img alt={t('editscreen.logo.placementHelp') as string} src="images/placement.png"/>
                                                        </div>
                                                    </Fragment>
                                                } 
                                                <Dropdown drop={isMobile ? 'up' : 'down'}>
                                                    <Dropdown.Toggle>
                                                        {selectedLogoPlaneData === undefined ? t('editscreen.logo.placementEmpty') : `${translate(selectedLogoPlaneData.name, i18n)} (${selectedLogoPlaneData.id})`}
                                                    </Dropdown.Toggle>
                                                    {
                                                        createDropdownPortal(
                                                            <Dropdown.Menu popperConfig={{strategy: 'absolute'}}>
                                                                {
                                                                    allowedLogoPlanes?.map((logoPlaneId, logoPlaneIndex) => {
                                                                        const hasLogo = logoArticle?.logos.findIndex((logo) => { return logo.position === logoPlaneId; }) !== -1;
        
                                                                        if (logoArticle !== undefined && hasLogo) {
                                                                            return null;
                                                                        }
            
                                                                        const logoPlaneData = detailProduct?.logoPlanes.find((logoPlane) => {return logoPlane.id === logoPlaneId;});
            
                                                                        if(logoPlaneData === undefined) {
                                                                            return null;
                                                                        }
                                                                    
                                                                        //@ts-ignore
                                                                        return <Dropdown.Item className={`${selectedLogoPlane === logoPlaneId ? 'dropdown-item-selected' : ''}`} key={logoPlaneIndex} onClick={() => {setSelectedLogoPlane(logoPlaneId);}}>
                                                                            <span>{`${translate(logoPlaneData.name, i18n)} (${logoPlaneId})`}</span>
                                                                        </Dropdown.Item>
                                                                    })
                                                                }
                                                            </Dropdown.Menu>,
                                                            //@ts-ignore
                                                            document.getElementById('root') 
                                                        )
                                                    }
                                                </Dropdown>
                                             </Fragment>
                                        }
                                        {
                                            selectedLogoPlane !== -1 && (selectedLogoPlaneHasNoLogo && additionalLogosAllowed) &&
                                            <Fragment>
                                                <Dropdown drop={isMobile ? 'up' : 'down'}>
                                                    <Dropdown.Toggle>
                                                        {selectedLogoAreaData === undefined ? t('editscreen.logo.sizeEmpty') : `${translate(selectedLogoAreaData.name, i18n)} - ${selectedLogoAreaData.minSize}-${selectedLogoAreaData.maxSize} cm²`}
                                                    </Dropdown.Toggle>

                                                    {
                                                    createDropdownPortal(
                                                        <Dropdown.Menu popperConfig={{strategy: 'absolute'}}>
                                                            {
                                                                props.library.logoAreas.map((logoArea, logoAreaIndex) => {
                                                                    if(logoArea.type !== activeSelectedLogoType) {
                                                                        return null;
                                                                    }

                                                                    const logoPlaneData = detailProduct?.logoPlanes.find((logoPlane) => {return logoPlane.id === selectedLogoPlane;});

                                                                    if(logoPlaneData === undefined) {
                                                                        return null;
                                                                    }

                                                                    let maxLogoSizeOnPlane = 0;
                                                                    if(activeSelectedLogoType === LogoType.Stitched && logoPlaneData.stitchSize !== undefined) {
                                                                        maxLogoSizeOnPlane = logoPlaneData.stitchSize.width * logoPlaneData.stitchSize.height;
                                                                    } else if(activeSelectedLogoType === LogoType.Transfer && logoPlaneData.transferSize !== undefined) {
                                                                        maxLogoSizeOnPlane = logoPlaneData.transferSize.width * logoPlaneData.transferSize.height;
                                                                    }

                                                                    if(logoArea.minSize > maxLogoSizeOnPlane) {
                                                                        return null;
                                                                    }
                                                                    
                                                                    return <Dropdown.Item className={`${selectedLogoSize === logoArea.id ? 'dropdown-item-selected' : ''}`} key={logoAreaIndex} onClick={() => {setSelectedLogoSize(logoArea.id);}}>
                                                                    <span>{`${translate(logoArea.name, i18n)} - ${logoArea.minSize}-${logoArea.maxSize} cm²`}</span>
                                                                </Dropdown.Item>
                                                                })
                                                            }
                                                        </Dropdown.Menu>,
                                                            //@ts-ignore
                                                            document.getElementById('root')
                                                        )
                                                    }
                                                </Dropdown>
                                            </Fragment>
                                        }
                                        {
                                            selectedLogoPlane !== -1 && selectedLogoSize !== '' && (selectedLogoPlaneHasNoLogo && additionalLogosAllowed) &&
                                            <Dropdown drop={isMobile ? 'up' : 'down'}>
                                                <Dropdown.Toggle>
                                                    {selectedLogoFile === null ? t('editscreen.logo.noLogo') : selectedLogoFile?.originalFilename}
                                                </Dropdown.Toggle>

                                                {
                                                    createDropdownPortal(
                                                        <Dropdown.Menu popperConfig={{strategy: 'absolute'}}>
                                                            {
                                                                //@ts-ignore
                                                                <Dropdown.Item className={`${selectedLogoFileIndex === -1 ? 'dropdown-item-selected' : ''}`}  key={-1} onClick={() => {setSelectedLogoFileIndex(-1);}}>{t('editscreen.logo.noLogo')}</Dropdown.Item>
                                                            }
                                                            {
                                                                props.config.logoFiles.map((logoFile, logoFileIndex) => {
                                                                    //@ts-ignore
                                                                    return <Dropdown.Item className={`${selectedLogoFileIndex === logoFileIndex ? 'dropdown-item-selected' : ''}`} key={logoFileIndex} onClick={() => {setSelectedLogoFileIndex(logoFileIndex);}}>
                                                                        <span>{logoFile.originalFilename}</span>
                                                                        {logoFile !== undefined && (logoFile.originalFilename.endsWith('.png') || logoFile.originalFilename.endsWith('.jpg') || logoFile.originalFilename.endsWith('.jpeg')) &&
                                                                            <div className={styles.logoListEntryImage}>
                                                                                <img src={getURL(GET_LOGO, {
                                                                                    id: props.config.code,
                                                                                    filename: logoFile.filename
                                                                                })} alt={logoFile.filename}></img>
                                                                            </div>
                                                                        }
                                                                    </Dropdown.Item>
                                                                })
                                                            }
                                                        </Dropdown.Menu>,
                                                        //@ts-ignore
                                                        document.getElementById('root')
                                                    )
                                                }
                                            </Dropdown>
                                        }
                                        {
                                            selectedLogoPlane !== -1 && selectedLogoSize !== '' && (selectedLogoPlaneHasNoLogo && additionalLogosAllowed) &&
                                            <Fragment>                                        
                                                <button className={`${styles.buttonPrimary} ${styles.buttonPlaceLogo}`} onClick={() => {
                                                    if(detailProduct !== undefined) {
                                                        const logoFileName = selectedLogoFile === null ? '' : selectedLogoFile.filename;
                                                        if(logoArticleIndex !== -1 && logoArticle !== undefined) {
                                                            const updatedProduct: ProductState = Object.assign({}, logoArticle);
                                                            const logoIndex = updatedProduct.logos.findIndex((logo) => {
                                                                return logo.position === selectedLogoPlane;
                                                            });
                                                            if(logoIndex === -1) {
                                                                updatedProduct.logos.push({
                                                                    position: selectedLogoPlane,
                                                                    filename: logoFileName,
                                                                    logoAreaId: selectedLogoSize,
                                                                    logoType: activeSelectedLogoType
                                                                });
                                                            } else {
                                                                updatedProduct.logos[logoIndex].filename = logoFileName;
                                                            }
                                                            props.onProductUpdate(logoArticleIndex, updatedProduct);
                                                        } else {
                                                            const newProduct = {
                                                                article: detailProduct.article,
                                                                logos: [
                                                                    {
                                                                        filename: logoFileName,
                                                                        position: selectedLogoPlane,
                                                                        logoAreaId: selectedLogoSize,
                                                                        logoType: activeSelectedLogoType
                                                                    }
                                                                ],
                                                                order: [
                                                                    { 
                                                                        amount: 1,
                                                                        size: detailProduct.sizes[0]
                                                                    }
                                                                ],
                                                                ventilationZipper: false
                                                            }

                                                            let targetIndex = props.config.products.length;
                                                            props.onProductAdd(detailProduct.article, detailProduct.sizes[0]);
                                                            props.onProductUpdate(targetIndex, newProduct);
                                                        }

                                                        setSelectedLogoPlane(-1);
                                                        setSelectedLogoSize('');
                                                        
                                                        if (isMobile && tabContainerRef.current) {
                                                            tabContainerRef.current.scrollTo({top: 0, behavior: 'smooth'});
                                                        }  
                                                    }
                                                }}>{logoArticle === undefined ? t('editscreen.logo.placeFirst') : t('editscreen.logo.place')}</button>
                                            </Fragment>    
                                        }
                                    </div>
                                    {isMobile &&                                                       
                                        <button data-testid="mymotion_save" className={`${styles.buttonSecondary} ${styles.buttonMobileSave}`} onClick={() => {setIsSavingMyMotion(true)}}>{t('editscreen.save')}</button>
                                    }
                            </div>
                            }
                            {isOrderScreen && props.ui.mobileOrderStep === 'order' &&
                                <div className={styles.tabContent}>
                                    <div className={styles.mobileOrderScreen}>
                                        <OrderscreenContainer scrollRef={tabContainerRef} eventRef={baseEventRef}></OrderscreenContainer>
                                    </div>
                                </div>
                            }
                            {isOrderScreen && props.ui.mobileOrderStep === 'customer' &&
                                <div className={styles.tabContent}>
                                    <div className={styles.mobileOrderScreen}>
                                        <CustomerscreenContainer></CustomerscreenContainer>
                                    </div>
                                </div>
                            }
                            {isOrderScreen && props.ui.mobileOrderStep === 'final' &&
                                <div className={styles.tabContent}>
                                    <div className={styles.mobileOrderScreen}>
                                        <FinalscreenContainer scrollRef={tabContainerRef} eventRef={baseEventRef}></FinalscreenContainer>
                                    </div>
                                </div>
                            }
                            {isOrderScreen && props.ui.mobileOrderStep === 'post' &&
                                <div className={styles.tabContent}>
                                    <div className={styles.mobileOrderScreen}>
                                        <PostscreenContainer></PostscreenContainer>
                                    </div>
                                </div>
                            }
                        </div>
                        <div className={`${styles.buttonContainer}`}>
                            <button data-testid="mymotion_save" className={`${styles.buttonWhite}`} onClick={() => {setIsSavingMyMotion(!isSavingMyMotion);}}><IconSave/>{t('editscreen.save')}</button>
                            {!isProducts && !isLogos && <button data-testid="mymotion_chooseProductsBottom" className={`${styles.buttonPrimary}`} onClick={() => {
                                setIsSelectingLogoProduct(false); 
                                setIsSelectingPreviewProduct1(true); 
                                setIsSelectingPreviewProduct2(false); 
                                setIsSelectingFromProducts(true); 
                                props.onUiSetEditScreenTab('products');
                            }}><IconChange/>{t('editscreen.chooseproduct')}</button>}
                            {!isProducts && isLogos && detailProduct !== undefined && <button className={`${styles.buttonPrimary}`} disabled={detailProductIsOnOrderList} onClick={() => { if(!detailProductIsOnOrderList) {props.onProductAdd(detailProduct.article, detailProduct.sizes[0])}}}>
                                {detailProductIsOnOrderList ? <IconCartBlack/> : <IconCart/>} {detailProductIsOnOrderList ? t('editscreen.addToOrderListAlreadyOn') : t('editscreen.addToOrderList')}
                            </button>}

                            {isProducts && <button data-testid="mymotion_orderListBottom" className={`${styles.buttonPrimary}`} onClick={() => { props.onUiSetDetailArticleIndex(0);props.onUiSetEditScreenTab('colors'); props.onUiGoTo('orderscreen')}}><IconCart/>{t('editscreen.orderlistView')}</button>}
                        </div>
                    </div>             
                </div>
                <div className={`${styles.saveMyMotionContainer} ${isSavingMyMotion ? styles.saveMyMotionContainerVisible : ''}`}>
                    <div className={`${styles.saveMyMotionContent}`}>
                        {isMobile && <LogoText size={'big'} center={false} black={true}/>}
                        <div className={styles.close} onClick={() => {
                            setIsSavingMyMotion(false);
                        }}><IconClose/></div>
                        <h1>{t('save.header')}</h1>
                        <p>{t('save.intro')}</p>
                        <div className={styles.choiceContainer}>
                            <div>
                                <p>{t('editscreen.baseColor')}</p>
                                <div>
                                    <div className={`${styles.colorSwatch} ${selectedBaseColor?.id === '06' ? styles.colorSwatchBorder : ''}`} style={{backgroundColor: selectedBaseColor?.color}}></div>
                                    <p>{selectedBaseColorName}</p>
                                </div>
                            </div>
                            <div>
                                <p>{t('editscreen.contrastColor')}</p>
                                <div>
                                    <div className={`${styles.colorSwatch} ${selectedContrastColor?.id === '06' ? styles.colorSwatchBorder : ''}`} style={{backgroundColor: selectedContrastColor?.color}}></div>
                                    <p>{selectedContrastColorName}</p>
                                </div>
                            </div>
                            <div>
                                <p>{t('editscreen.straussLogo')}</p>
                                <div>
                                    {props.config.straussLogo === StraussLogoType.Standard && <IconStandard/>}
                                    {props.config.straussLogo === StraussLogoType.StitchedWhite && <IconWhite/>}
                                    {props.config.straussLogo === StraussLogoType.StitchedBlack && <IconBlack/>}
                                    <p>{selectedStraussLogoName}</p>
                                </div>
                            </div>
                        {
                            props.config.logoFiles.length > 0 && 
                                <div>
                                    <p>{t('editscreen.logo.header')}</p>
                                    <div className={styles.logoContainer}>
                                    {
                                        props.config.logoFiles.map((logoFile, logoIndex) => {
                                            return <div className={`${styles.logoContainerEntry}`} key={logoIndex}>
                                                {(logoFile.originalFilename.endsWith('.png') || logoFile.originalFilename.endsWith('.jpg') || logoFile.originalFilename.endsWith('.jpeg')) &&
                                                    <div className={styles.logoListEntryImage}>
                                                        <img src={getURL(GET_LOGO, {
                                                            id: props.config.code,
                                                            filename: logoFile.filename
                                                        })} alt={logoFile.filename}></img>
                                                    </div>
                                                }
                                                <div>{logoFile.originalFilename}</div>
                                            </div>;
                                        })                     
                                    }
                                    </div>
                                </div>
                        }
                        </div>

                        <h1>{t('save.choices')}</h1>
                        <div className={styles.saveProductContainerWrapper} ref={saveContainerRef}>
                            <PreviewContainer
                                className={styles.fullCanvas}
                                eventRef={baseEventRef}
                                canvasRef={saveCanvasRef}
                                >
                                {
                                    props.config.products.map((product, productIndex) => {
                                        let article = props.library.products.find((article) => {return article.article === product.article});
                                        if (article === undefined) {
                                            return null;
                                        }
                                        
                                        return <PreviewContainerModel key={product.article} divRef={saveDivRefs[productIndex]} library={props.library} model={product.article} thumbnail={true} modelData={article.modelDataThumbnail !== undefined ? article.modelDataThumbnail : article.modelData} xOffset={0} baseColorId={props.config.baseColorId} contrastColorId={props.config.contrastColorId} straussLogo={props.config.straussLogo} showPlanes={false}/>;
                                    })                     
                                }
                            </PreviewContainer>
                            <div className={styles.productContainer} ref={saveEventRef}>
                                {
                                    props.config.products.length > 0 &&
                                    props.config.products.map((product, productIndex) => {
                                        let article = props.library.products.find((article) => {return article.article === product.article});
                                        if (article === undefined) {
                                            return null;
                                        }
                                        return <div className={`${styles.productEntry}`} key={productIndex}>
                                            <div className={styles.productPreview} ref={saveDivRefs[productIndex]}>
                                            
                                            </div>
                                            <div>
                                                <h1>{translate(article.name, i18n)}</h1>               
                                                {article.hasVentilationZipper && <h2>{t('editscreen.invVentilationShort')}</h2>}
                                                <p dangerouslySetInnerHTML={{__html: t('additionalProducts.price', {price: getPricingText(i18n.language, props.ui.country, article.pricing[0].pricing)})}}></p>
                                                <p dangerouslySetInnerHTML={{__html: t('additionalProducts.fromAmount', {pieces: getMinimumOrderAmount(article, props.library)})}}></p>
                                            </div>
                                        </div>;
                                    })                     
                                } {
                                    props.config.products.length === 0 &&
                                    <p>{t('orderscreen.subHeaderEmpty')}</p>
                                }
                            </div>
                        </div>
                        
                        <h1>{t('save.code')}</h1>
                        <div className={styles.codeContainer}>
                            <input type="text" value={props.ui.code} disabled={true}/>
                            <button className={`${styles.buttonPrimary}`} disabled={props.ui.isLoadingConfig} onClick={() => {
                                copyTextToClipboard(props.ui.code)
                                .then(() => {
                                    setIsCodeCopied(true);
                                    setTimeout(() => {
                                        setIsCodeCopied(false);
                                    }, 3000);
                                })
                                .catch((err) => {
                                    console.log(err);
                                });
                            }}>{t('save.copyCode')}</button>
                        </div>
                        <p className={`${styles.copied} ${isCodeCopied ? styles.copiedVisible : ''}`}>{t('save.copied')}</p>
                        <p className={styles.codeInfo}>{t('save.info')}</p>
                    </div>
                </div>
                <div className={styles.mobileUiBackground}>
                </div>
                <div className={styles.mobileUi}>
                    <div className={styles.mobileUiTabs}>
                        <button data-testid="mymotion_chooseProductsTop" className={`${styles.tabButton} ${isProducts ? styles.tabButtonActive : ''}`} onClick={() => {
                            props.onUiGoTo('editscreen'); 
                            if(isProducts) {
                                props.onUiSetEditScreenTab('');
                            } else {
                                setIsSelectingLogoProduct(false); 
                                setIsSelectingFromProducts(false); 
                                setIsSelectingPreviewProduct1(true); 
                                props.onUiSetEditScreenTab('products'); 
                            }
                        }}><IconChange/>{t('editscreen.products')}</button>
                        <button data-testid="mymotion_chooseColours" className={`${styles.tabButton} ${isColors ? styles.tabButtonActive : ''}`} onClick={() => {
                            props.onUiGoTo('editscreen'); 
                            if(isColors) {
                                props.onUiSetEditScreenTab('');
                            } else {
                                props.onUiSetDetailArticleIndex(0);
                                props.onUiSetEditScreenTab('colors'); 
                            }
                        }}><div className={styles.colorIcon}></div>{t('editscreen.colors')}</button>
                        <button data-testid="mymotion_chooseLogo" className={`${styles.tabButton} ${isLogos ? styles.tabButtonActive : ''}`} onClick={() => {
                            props.onUiGoTo('editscreen');                             
                            if(isLogos) {
                                props.onUiSetEditScreenTab('');
                            } else {
                                if(props.ui.detailArticleIndex === 0) { 
                                    props.onUiSetDetailArticleIndex(1);
                                }
                                props.onUiSetEditScreenTab('logos'); 
                            }
                        }}><IconShirt/>{t('editscreen.logos')}</button>
                        <button data-testid="mymotion_orderListBottom" className={`${styles.tabButton} ${isOrderScreen ? styles.tabButtonActive : ''}`} onClick={() => {
                            props.onUiGoTo('editscreen'); 
                            if(isOrderScreen) {
                                props.onUiSetEditScreenTab('');
                            } else {
                                props.onUiSetEditScreenTab('orderscreen');
                            }
                            }}>
                                <IconCart/>
                                {t('editscreen.orderlist')}
                                {props.config.products.length > 0 &&
                                    <span className={styles.orderCount}>{props.config.products.length}</span>
                                }
                            </button>
                    </div>
                </div>  
                <div className={styles.footer}><LanguageSwitcherContainer/><span className={styles.footerSeparator}></span><LinkFooterContainer fullWidth={true}/></div>
            </Container>
        </Fragment>
    );
};